<script setup lang="ts">
// Métadonnées de la page des circulaires
import type { TabsItem } from '@nuxt/ui'

useHead({
  title: 'Circulaires - Glouton',
  meta: [
    { name: 'description', content: 'Consultez les circulaires des épiceries et découvrez les meilleures aubaines pour économiser sur vos courses.' },
    { property: 'og:title', content: 'Circulaires - Glouton' },
    { property: 'og:description', content: 'Consultez les circulaires des épiceries et découvrez les meilleures aubaines pour économiser sur vos courses.' },
  ],
})

const items = ref<TabsItem[]>([
  {
    label: 'En vigueur',
    value: 'now',
  },
  {
    label: 'À venir',
    value: 'future',
  },
])

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/flyers',
    fr: '/circulaires',
  },
})

definePageMeta({
  name: 'FlyersPage',
})
const route = useRoute()

const userStore = useUserStore()
const baseStore = useBaseStore()
const config = useRuntimeConfig()
const pushNotifications = usePushNotifications()
const toast = useToast()

const cacheStore = useCacheStore()
const modalStore = useModalStore()
const overlay = useOverlay()
const localePath = useLocalePath()

const FLYERS_PUSH_ALERT_DISMISS_KEY = 'hide_flyers_push_alert'

const pending = ref(false)
const showHelp = ref(false)
const showPushAlert = ref(false)
const isAuthorizingPush = ref(false)
const flyers = ref([])
const user_flyers = ref([])

function resolvePeriod(value: unknown): 'now' | 'future' | null {
  return value === 'now' || value === 'future' ? value : null
}

const period = ref(
  resolvePeriod(route.query.period)
  || cacheStore.getCache('FlyersPage-period')
  || 'now',
)

const tabs = computed(() => {
  return [
    {
      label: 'Circulaires',
      to: localePath({ name: 'FlyersPage' }),
      active: route.name === 'FlyersPage___fr' || route.name === 'FlyerPage___fr',
      extra: false,
    },
    {
      label: 'Produits suivis',
      to: localePath({ name: 'TrackedIngredientsPage' }),
      active: route.name === 'TrackedIngredientsPage___fr',
      extra: true,
    },
  ]
})

// fetch everything at once, no pagination
async function fetchFlyers() {
  pending.value = true
  try {
    const newData = await $fetch(`${config.public.apiUrl}/flyer-reader/get-flyers`, {
      query: {
        period: period.value,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (newData) {
      flyers.value = newData.flyers || []
      user_flyers.value = newData.user_flyers || []
    }
  }
  finally {
    pending.value = false
  }
}

function isPermissionUndetermined(status: string): boolean {
  return status === 'not_determined' || status === 'prompt' || status === 'prompt-with-rationale'
}

function updateShowPushAlert(): void {
  if (!pushNotifications.isNative.value || !userStore.user) {
    showPushAlert.value = false
    return
  }

  if (localStorage.getItem(FLYERS_PUSH_ALERT_DISMISS_KEY) === 'true') {
    showPushAlert.value = false
    return
  }

  const status = pushNotifications.permissionStatus.value

  if (!isPermissionUndetermined(status)) {
    showPushAlert.value = false
    return
  }

  showPushAlert.value = true
}

function closePushAlert(): void {
  showPushAlert.value = false
  localStorage.setItem(FLYERS_PUSH_ALERT_DISMISS_KEY, 'true')
}

async function handleAuthorizeNotifications(): Promise<void> {
  if (!pushNotifications.isNative.value || isAuthorizingPush.value) {
    return
  }

  isAuthorizingPush.value = true

  try {
    const result = await pushNotifications.setNotificationsEnabled(true)

    if (result === 'enabled') {
      toast.add({
        title: 'Notifications activées',
        description: 'Vous recevrez les alertes Glouton sur cet appareil.',
        icon: 'i-lucide-bell',
        color: 'success',
      })
      updateShowPushAlert()
      return
    }

    if (result === 'permission_denied') {
      toast.add({
        title: 'Notifications non autorisées',
        description: 'Vous avez refusé les notifications. Vous pourrez les activer plus tard dans les réglages de votre téléphone.',
        icon: 'i-lucide-bell-off',
        color: 'warning',
      })
      updateShowPushAlert()
      return
    }

    toast.add({
      title: 'Synchronisation impossible',
      description: 'Impossible d\'enregistrer cet appareil sur le serveur. Vérifiez votre connexion et réessayez.',
      icon: 'i-lucide-bell-off',
      color: 'error',
    })
  }
  finally {
    isAuthorizingPush.value = false
  }
}

// ✅ run only on client when mounted
onMounted(async () => {
  fetchFlyers()

  if (pushNotifications.isNative.value && userStore.user) {
    await pushNotifications.fetchPreferences()
    await pushNotifications.checkPermission()
    updateShowPushAlert()
  }
})

watch(() => route.query.period, (queryPeriod) => {
  const resolved = resolvePeriod(queryPeriod)
  if (resolved) {
    period.value = resolved
  }
})

watch(period, (newPeriod) => {
  // Sauvegarder l'état dans le cache quand il change
  cacheStore.setCache('FlyersPage-period', newPeriod)
  fetchFlyers()
})

// Watch for user login state changes and refetch flyers
watch(() => userStore.user, async (newUser, oldUser) => {
  // If user was not logged in before but is now logged in
  if (!oldUser && newUser) {
    fetchFlyers()
  }

  if (pushNotifications.isNative.value && newUser) {
    await pushNotifications.fetchPreferences()
    await pushNotifications.checkPermission()
    updateShowPushAlert()
    return
  }

  showPushAlert.value = false
}, { immediate: false })
</script>

<template>
  <div id="FlyersPage">
    <TopNav
      title="Circulaires"
      description="Consultez les rabais des épiceries et repérez les meilleures aubaines avec nos scores sur 10."
      access="L'accès à cet outil est gratuit avec un compte Glouton."
      :help-status="showHelp"
      class="max-w-7xl mx-auto"
      :tabs="tabs"
      :show-help-button="true"
      @help-button-click="showHelp = !showHelp"
    />
    <template v-if="!showHelp">
      <ClientOnly>
        <div v-if="showPushAlert" class="w-full max-w-7xl mx-auto px-0 md:px-8">
          <UAlert
            class="rounded-none md:rounded-lg mt-0 md:my-4"
            color="tertiary"
            variant="solid"
            description="Recevez une notification dès que nos évaluations des rabais sont disponibles chaque semaine!"
            icon="i-line-md-bell-loop"
            :actions="[
              {
                label: 'Autoriser les notifications',
                color: 'primary',
                variant: 'solid',
                class: 'text-xs lg:text-sm',
                loading: isAuthorizingPush,
                disabled: isAuthorizingPush,
                onClick: () => handleAuthorizeNotifications(),
              },
            ]"
            :ui="{
              icon: 'size-8',
              description: 'text-sm lg:text-base',
              close: 'text-white',
            }"
            close
            @update:open="closePushAlert"
          />
        </div>
      </ClientOnly>

      <UContainer id="FlyersPage-anchor" class="pb-10">
        <PromoBanner class="my-6" message-type="message_flyers" />
        <UTabs v-model="period" color="primary" :content="false" :items="items" class="my-4" size="xl" :ui="{ list: ' bg-white' }" />

        <div>
          <div class="flex items-center justify-between text-sm md:text-base font-semibold border-b-1 border-poivre-200 uppercase px-1 pb-2 text-poivre mb-4">
            <h3 class="items-center flex">
              <UIcon name="i-mdi-star" class="mr-1 text-poivre-800 hidden sm:inline" /> Mes favoris
            </h3>
            <div>
              <UButton color="secondary" size="sm" :to="localePath({ name: 'BestItemsPage' })" class="normal-case">
                Afficher les meilleurs rabais
              </UButton>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4 md:gap-6 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6">
            <template v-for="(user_flyer, index) in user_flyers" :key="user_flyer.id">
              <div class="flex flex-col h-full">
                <FlyerCard :flyer="user_flyer" />
              </div>
              <!-- Ad after 4 items (mobile/sm: 2 columns) -->
              <div
                v-if="[4, 10, 16, 22].includes(index + 1) && baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
                class="flex flex-col col-span-2"
              >
                <DisplayAd
                  dfp-ad-unit-path="glouton/circulaires"
                  :sizes="[300, 250]"
                  custom-class="h-full"
                />
              </div>
              <!-- Ad after 6 items (md: 3 columns) -->
            </template>
          </div>
          <p v-if="period == 'now' && user_flyers.length === 0 && userStore.user" class="text-poivre-500">
            Ajoutez des commerces à vos favoris pour voir leurs circulaires apparaître ici.
          </p>
          <p v-else-if="period == 'future' && user_flyers.length === 0 && userStore.user" class="text-poivre-500">
            Aucune circulaire disponible pour l'instant parmi vos commerces favoris.
          </p>
          <p v-else-if="!userStore.user" class="text-poivre-500">
            <a href="#" class="underline font-medium text-primary" @click.prevent="modalStore.openLoginModal">Connectez-vous</a> ou <ULink class="underline font-medium text-primary inline-flex" :to="localePath({ name: 'RegisterPage' })">
              créez un compte
            </ULink> gratuitement pour voir nos évaluations des rabais et suivre vos commerces favoris.
          </p>

          <DisplayAd
            v-if="baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user)"
            dfp-ad-unit-path="glouton/circulaires"
            :sizes="[[728, 90], [320, 50], [300, 250], [970, 250]]"
            :size-mapping="[[[0, 0], [[320, 50], [300, 250]]], [[768, 0], [[728, 90]]], [[1024, 0], [[970, 250], [728, 90]]]]"
            custom-class="my-6"
          />

          <h3 class="text-sm md:text-base font-semibold border-b-1 border-poivre-200 uppercase px-1 pb-2 text-poivre mb-4 mt-6">
            Les autres circulaires
          </h3>
          <div class="grid grid-cols-2 gap-4 md:gap-6 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6">
            <template v-for="(flyer, index) in flyers" :key="flyer.id">
              <div class="flex flex-col h-full">
                <FlyerCard :flyer="flyer" />
              </div>
              <!-- Ad after 2 items (mobile/sm: 2 columns) -->
              <div
                v-if="[4, 10, 16, 22].includes(index + 1) && baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
                class="flex flex-col col-span-2"
              >
                <DisplayAd
                  dfp-ad-unit-path="glouton/circulaires"
                  :sizes="[300, 250]"
                  custom-class="h-full"
                />
              </div>
              <!-- Ad after 3 items (md: 3 columns) -->
            </template>
          </div>
          <p v-if="flyers.length === 0" class="text-poivre-500">
            Aucune autre circulaire disponible.
          </p>
        </div>
      </UContainer>
    </template>

    <!-- Aide des circulaires -->
    <FlyersHelp v-if="showHelp" class="py-6" />
  </div>
</template>
